/**
 * Shared formatting utilities for call data displayed across the dashboard.
 * Used by the calls list, call detail, and analytics drilldown views.
 */

/** Format call duration. Returns "—" for null/undefined. e.g. "2m 05s" or "45s" */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  }
  return `${seconds}s`;
}

/** Format a timestamp for table rows: "Mar 2, 10:30 AM" */
export function formatTimestampShort(date: Date | string | null | undefined): string {
  if (!date) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(date));
  } catch {
    return '—';
  }
}

/** Format a timestamp for detail views: "Mar 2, 2026, 10:30 AM" */
export function formatTimestampFull(date: Date | string | null | undefined): string {
  if (!date) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(date));
  } catch {
    return '—';
  }
}

/** Format a timestamp with full precision for call detail pages: "March 2, 2026 at 10:30:45 AM PST" */
export function formatTimestampDetailed(date: Date | string | null | undefined): string {
  if (!date) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      timeZoneName: 'short',
    }).format(new Date(date));
  } catch {
    return '—';
  }
}
